import React from "react";
import * as Mui from "@mui/material";
import { makeStyles } from "tss-react/mui";
import * as Icons from "react-feather";

const useStyles = makeStyles()((theme) => ({
	paper: { maxWidth: 560, width: "100%", borderRadius: 8 },
	// Uniform 24px padding; body items stack with 16px gaps, then a 32px gap before the actions
	// (matches the Profile Photo modal / Figma 722:6803).
	content: {
		padding: 24,
		display: "flex",
		flexDirection: "column",
		gap: 32,
		"&:first-child": { paddingTop: 24 },
	},
	body: { display: "flex", flexDirection: "column", gap: theme.spacing(2), width: "100%" },
	title: { display: "flex", alignItems: "center", justifyContent: "space-between" },
	titleText: { fontSize: 24, lineHeight: "32px", fontWeight: 700, color: theme.palette.custom.textPrimary },
	closeButton: { color: theme.palette.custom.textSecondary },
	divider: { width: "100%" },
	// Field label above the input — 14px regular #5F6368 (Figma Fields/Title).
	fieldLabel: {
		fontSize: 14,
		lineHeight: "20px",
		color: theme.palette.custom.textSecondary,
		marginBottom: 8,
	},
	input: {
		"& .MuiOutlinedInput-root": { borderRadius: 4 },
		"& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.custom.borderCard },
		"& .MuiOutlinedInput-input": { padding: "12px 16px" },
	},
	actions: { display: "flex", justifyContent: "flex-end", gap: 8, padding: 0 },
	// Both actions 36px tall; secondary has a 2px teal border, primary is teal fill (Figma).
	secondaryButton: {
		height: 36,
		boxSizing: "border-box",
		border: `2px solid ${theme.palette.custom.tertiaryAction}`,
		color: theme.palette.custom.tertiaryAction,
		borderRadius: 8,
		fontWeight: 590,
		textTransform: "none",
		padding: "0 16px",
		"&:hover": { border: `2px solid ${theme.palette.custom.tertiaryAction}` },
	},
	primaryButton: {
		height: 36,
		boxSizing: "border-box",
		backgroundColor: theme.palette.custom.tertiaryAction,
		color: "#ffffff",
		border: "2px solid transparent",
		borderRadius: 8,
		fontWeight: 590,
		textTransform: "none",
		padding: "0 16px",
		"&:hover": { backgroundColor: theme.palette.custom.tertiaryAction },
	},
}));

interface Props {
	open: boolean;
	testimonialData: { quote: string; author: string };
	onClose: () => void;
	onSave: () => void;
	onUpdateData: (data: { quote: string; author: string }) => void;
}

export const TestimonialModal: React.FC<Props> = ({
	open,
	testimonialData,
	onClose,
	onSave,
	onUpdateData,
}) => {
	const { classes } = useStyles();
	return (
		<Mui.Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth classes={{ paper: classes.paper }}>
			<Mui.DialogContent className={classes.content}>
				<div className={classes.body}>
					<div className={classes.title}>
						<Mui.Typography className={classes.titleText}>Add Testimonial</Mui.Typography>
						<Mui.IconButton size="small" className={classes.closeButton} onClick={onClose} aria-label="Close">
							<Icons.X size={24} />
						</Mui.IconButton>
					</div>
					<Mui.Divider className={classes.divider} />

					<div>
						<Mui.Typography className={classes.fieldLabel}>Client Name</Mui.Typography>
						<Mui.TextField
							fullWidth
							variant="outlined"
							className={classes.input}
							value={testimonialData.author}
							onChange={(e) => onUpdateData({ ...testimonialData, author: e.target.value })}
						/>
					</div>

					<div>
						<Mui.Typography className={classes.fieldLabel}>Testimonial Message</Mui.Typography>
						<Mui.TextField
							fullWidth
							multiline
							minRows={4}
							variant="outlined"
							className={classes.input}
							value={testimonialData.quote}
							onChange={(e) => onUpdateData({ ...testimonialData, quote: e.target.value })}
						/>
					</div>
				</div>

				<Mui.DialogActions className={classes.actions} disableSpacing>
					<Mui.Button className={classes.secondaryButton} onClick={onClose}>
						Cancel
					</Mui.Button>
					<Mui.Button className={classes.primaryButton} onClick={onSave}>
						Add Testimonial
					</Mui.Button>
				</Mui.DialogActions>
			</Mui.DialogContent>
		</Mui.Dialog>
	);
};
